import type {
  AdminEvent,
  AdminSettings,
  FormConfig,
  PublicEvent,
  RoleBucket,
  SaveRequest,
  SaveResponse,
  VolunteerRecord,
} from "../shared/types";

const BASE = (import.meta.env.VITE_API_BASE_URL ?? "").replace(/\/$/, "");

export class ApiError extends Error {
  status: number;
  fields?: Record<string, string>;
  constructor(message: string, status: number, fields?: Record<string, string>) {
    super(message);
    this.status = status;
    this.fields = fields;
  }
}

async function req<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${BASE}/api${path}`, {
    credentials: "include",
    headers: init?.body ? { "Content-Type": "application/json" } : undefined,
    ...init,
  });
  const text = await res.text();
  let data: any = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    /* 非 JSON（例如 CSV）不會走到這裡 */
  }
  if (!res.ok || (data && data.ok === false)) {
    throw new ApiError(
      (data && data.error) || `請求失敗（${res.status}）`,
      res.status,
      data && data.fields,
    );
  }
  return data as T;
}

// ---------- 前台 ----------
export const getConfig = () => req<FormConfig>("/config");
export const getEvents = () => req<{ events: PublicEvent[] }>("/events");
export const lookupVolunteer = (email: string) =>
  req<{ ok: true; volunteer: VolunteerRecord | null }>("/volunteer/lookup", {
    method: "POST",
    body: JSON.stringify({ email }),
  });
export const saveVolunteer = (payload: SaveRequest) =>
  req<SaveResponse>("/volunteer/save", { method: "POST", body: JSON.stringify(payload) });

// ---------- 管理後台 ----------
export const adminLogin = (username: string, password: string) =>
  req<{ ok: true; username: string }>("/admin/login", {
    method: "POST",
    body: JSON.stringify({ username, password }),
  });
export const adminLogout = () => req<{ ok: true }>("/admin/logout", { method: "POST" });
export const adminMe = () => req<{ ok: true; username: string }>("/admin/me");

export interface AdminVolunteerListItem extends VolunteerRecord {
  events: Array<{
    id: number;
    title: string;
    location: string | null;
    eventDate: string | null;
    creditHours: number;
    category: "morning_reading" | "charity";
    bucket: RoleBucket;
    waitlistPosition: number;
  }>;
}
export const adminListVolunteers = (params: Record<string, string>) => {
  const qs = new URLSearchParams(params).toString();
  return req<{ ok: true; minHours: number; volunteers: AdminVolunteerListItem[] }>(
    `/admin/volunteers${qs ? `?${qs}` : ""}`,
  );
};
export const adminUpdateVolunteer = (id: number, payload: SaveRequest & { status: string }) =>
  req<{ ok: true; volunteer: VolunteerRecord }>(`/admin/volunteers/${id}`, {
    method: "PUT",
    body: JSON.stringify(payload),
  });
export const adminDeleteVolunteer = (id: number) =>
  req<{ ok: true }>(`/admin/volunteers/${id}`, { method: "DELETE" });

export const adminListEvents = () => req<{ ok: true; events: AdminEvent[] }>("/admin/events");
export const adminCreateEvent = (payload: Partial<AdminEvent>) =>
  req<{ ok: true; event: AdminEvent }>("/admin/events", {
    method: "POST",
    body: JSON.stringify(payload),
  });
export const adminUpdateEvent = (id: number, payload: Partial<AdminEvent>) =>
  req<{ ok: true; event: AdminEvent }>(`/admin/events/${id}`, {
    method: "PUT",
    body: JSON.stringify(payload),
  });
export const adminDeleteEvent = (id: number) =>
  req<{ ok: true }>(`/admin/events/${id}`, { method: "DELETE" });
export const adminEventRoster = (id: number) =>
  req<{
    ok: true;
    roster: Array<{
      id: number;
      name: string;
      primaryEmail: string;
      phone: string;
      status: string;
      totalHours: number;
      bucket: RoleBucket;
      waitlistPosition: number;
    }>;
  }>(`/admin/events/${id}/roster`);

export const adminGetScheduleOverrides = () =>
  req<{ ok: true; at: string | null; data: Record<string, "in" | "wait"> }>(
    "/admin/schedule-overrides",
  );
export const adminSaveScheduleOverrides = (data: Record<string, "in" | "wait">) =>
  req<{ ok: true; at: string; count: number }>("/admin/schedule-overrides", {
    method: "PUT",
    body: JSON.stringify({ data }),
  });

export const adminGetSettings = () =>
  req<{ ok: true; settings: AdminSettings }>("/admin/settings");
export const adminUpdateSettings = (payload: Partial<AdminSettings>) =>
  req<{ ok: true; settings: AdminSettings }>("/admin/settings", {
    method: "PUT",
    body: JSON.stringify(payload),
  });

export function adminExportUrl(kind: string): string {
  return `${BASE}/api/admin/export/${kind}`;
}
